import { useMutation } from "react-query";

import axios from "axios";
import { AUTOMATION_SERVICE_ADDRESS } from "constants/app.constants";

export default function useDownloadAutomationResult() {
  return useMutation({
    mutationFn: async ({
      jobId,
      automationName
    }: {
      jobId: string;
      automationName?: string;
    }): Promise<string> => {
      if (!jobId) throw new Error("No job ID provided");

      const response = await axios.get(
        `${AUTOMATION_SERVICE_ADDRESS}/automation/job/${jobId}/download`,
        {
          responseType: "blob",
          headers: {
            "Content-Type": "application/json"
          }
        }
      );

      if (response.status !== 200) {
        throw new Error(`Failed to download job result: ${response.statusText}`);
      }

      // Create a blob and trigger download
      const url = window.URL.createObjectURL(
        new Blob([response.data], { type: "application/zip" })
      );
      const link = document.createElement("a");
      link.href = url;

      // Helper function to sanitize filename
      const sanitizeFilename = (name: string): string => {
        return name
          .replace(/[^a-zA-Z0-9\-_\s]/g, "") // Remove special characters
          .replace(/\s+/g, "_") // Replace spaces with underscores
          .trim();
      };

      // Generate timestamp for unique filenames
      const timestamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);

      // Extract filename from Content-Disposition header or use automation name/jobId as fallback
      const contentDisposition = response.headers["content-disposition"];
      let filename = automationName
        ? `${sanitizeFilename(automationName)}_${timestamp}.zip`
        : `${jobId}_${timestamp}.zip`;

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);

      return "Download completed";
    }
  });
}
